import { useState } from 'react';
import { Chapter, Lesson } from '../../../mocks/courses';

interface Props {
  chapters: Chapter[];
  totalDuration: string;
  canStudy: boolean;
  onRequirePurchase: () => void;
  onStartLearning: (lessonId?: string) => void;
}

const lessonTypeIcon = (type: Lesson['type']) => {
  switch (type) {
    case 'video': return 'ri-play-circle-line';
    case 'quiz': return 'ri-questionnaire-line';
    case 'project': return 'ri-tools-line';
    case 'reading': return 'ri-article-line';
    default: return 'ri-file-line';
  }
};

const lessonTypeColor = (type: Lesson['type']) => {
  switch (type) {
    case 'video': return 'text-violet-500';
    case 'quiz': return 'text-amber-500';
    case 'project': return 'text-emerald-500';
    case 'reading': return 'text-sky-500';
    default: return 'text-slate-400';
  }
};

const lessonTypeLabel = (type: Lesson['type']) => {
  switch (type) {
    case 'video': return '影片';
    case 'quiz': return '測驗';
    case 'project': return '專案';
    case 'reading': return '閱讀';
    default: return '';
  }
};

const CourseSyllabus = ({ chapters, totalDuration, canStudy, onRequirePurchase, onStartLearning }: Props) => {
  const [openChapters, setOpenChapters] = useState<Set<string>>(new Set(['ch1']));

  const toggleChapter = (id: string) => {
    setOpenChapters((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const expandAll = () => {
    setOpenChapters(new Set(chapters.map((c) => c.id)));
  };

  const collapseAll = () => {
    setOpenChapters(new Set());
  };

  const totalLessons = chapters.reduce((acc, ch) => acc + ch.lessons.length, 0);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-bold text-[#0A1628]">課程大綱</h2>
          <p className="text-slate-400 text-sm mt-1">
            {chapters.length} 章節 · {totalLessons} 節課 · 總時長 {totalDuration}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={expandAll}
            className="text-xs text-violet-600 font-medium cursor-pointer hover:text-violet-700 whitespace-nowrap transition-colors"
          >
            全部展開
          </button>
          <span className="text-slate-200">|</span>
          <button
            onClick={collapseAll}
            className="text-xs text-slate-400 font-medium cursor-pointer hover:text-slate-600 whitespace-nowrap transition-colors"
          >
            全部收起
          </button>
        </div>
      </div>

      {/* Chapters */}
      <div className="border border-slate-200 rounded-xl overflow-hidden">
        {chapters.map((chapter, idx) => {
          const isOpen = openChapters.has(chapter.id);
          const chapterLessons = chapter.lessons;
          const previewCount = chapterLessons.filter((l) => l.preview).length;

          return (
            <div key={chapter.id} className={idx > 0 ? 'border-t border-slate-200' : ''}>
              {/* Chapter header */}
              <button
                onClick={() => toggleChapter(chapter.id)}
                className="w-full flex items-center justify-between px-5 py-4 bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer text-left group"
              >
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5 transition-colors ${isOpen ? 'bg-violet-100' : 'bg-slate-200 group-hover:bg-slate-300'}`}>
                    <span className={`text-xs font-bold ${isOpen ? 'text-violet-600' : 'text-slate-500'}`}>
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[#0A1628] text-sm leading-snug">{chapter.title}</h3>
                    <p className="text-slate-400 text-xs mt-0.5 leading-relaxed">{chapter.description}</p>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="text-xs text-slate-400">{chapterLessons.length} 節課</span>
                      {previewCount > 0 && (
                        <span className="text-xs text-violet-500 font-medium flex items-center gap-1">
                          <i className="ri-eye-line text-xs" />
                          {previewCount} 節免費試看
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 ml-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-slate-400 text-lg" />
                </div>
              </button>

              {/* Lessons list */}
              {isOpen && (
                <div className="bg-white">
                  {chapterLessons.map((lesson, lessonIdx) => (
                    <div
                      key={lesson.id}
                      onClick={() => {
                        const locked = !canStudy && !lesson.preview;
                        if (locked) {
                          onRequirePurchase();
                          return;
                        }
                        onStartLearning(lesson.id);
                      }}
                      className={`flex items-center gap-3 px-5 py-3 transition-colors cursor-pointer group/lesson ${lessonIdx > 0 ? 'border-t border-slate-100' : ''} ${
                        !canStudy && !lesson.preview ? 'bg-slate-50/60 hover:bg-slate-100/80' : 'hover:bg-slate-50'
                      }`}
                    >
                      {/* Type icon */}
                      <div className={`w-7 h-7 flex items-center justify-center flex-shrink-0 ${lessonTypeColor(lesson.type)}`}>
                        <i className={`${!canStudy && !lesson.preview ? 'ri-lock-line text-slate-400' : lessonTypeIcon(lesson.type)} text-base`} />
                      </div>

                      {/* Lesson title */}
                      <div className="flex-1 min-w-0">
                        <span className={`text-sm ${lesson.preview ? 'text-violet-600 font-medium group-hover/lesson:text-violet-700' : 'text-slate-600'} leading-snug`}>
                          {lesson.title}
                        </span>
                      </div>

                      {/* Right side */}
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-xs text-slate-300 bg-slate-100 px-2 py-0.5 rounded-full">
                          {lessonTypeLabel(lesson.type)}
                        </span>
                        {!canStudy && !lesson.preview && (
                          <span className="text-xs text-amber-600 font-semibold bg-amber-50 px-2 py-0.5 rounded-full flex items-center gap-1 whitespace-nowrap">
                            <i className="ri-lock-2-line text-xs" />
                            需解鎖
                          </span>
                        )}
                        {lesson.preview && (
                          <span className="text-xs text-violet-500 font-semibold bg-violet-50 px-2 py-0.5 rounded-full flex items-center gap-1 whitespace-nowrap">
                            <i className="ri-eye-line text-xs" />
                            免費試看
                          </span>
                        )}
                        <span className="text-xs text-slate-400 whitespace-nowrap">{lesson.duration}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CourseSyllabus;
